import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
    ActionItem,
    ActionItemModifier,
    ActionItemStyle,
    ShActionItemComponent
} from '../../../../elements/components/action-item/action-item.component';

import { SharedLanguageService } from '@language';
import { ColumnDef } from '../../../list-view/entities/column-definition.entity';
import { fieldKeyToDefaultSortOptionKey } from '../../../list-view/helpers/field-sort-option.helper';
import { SortOptionsHelper } from '../../../list-view/helpers/sort-options.helper';
import { SortDirection } from '../display-settings-bar/entities/sort-direction.entity';
import { SortInfo } from '../display-settings-bar/entities/sort-info.entity';
import { SortType } from '../display-settings-bar/entities/sort-type.entity';

@Component({
    standalone: true,
    selector: 'sh-column-sort',
    templateUrl: './column-sort.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, TranslateModule, ShActionItemComponent]
})
export class ColumnSortComponent {
    private readonly DEFAULT_SORT_DIRECTION: SortDirection = SortDirection.ASC;

    private readonly translateService: SharedLanguageService = inject(SharedLanguageService);

    columnDefinition = input.required<ColumnDef<any, any>>();
    currentSort = input.required<SortInfo>();

    sortInfoChanged = output<SortInfo>();

    sortAction = computed(() => this.getSortAction(this.columnDefinition(), this.currentSort()));
    isSortable = computed(() => this.columnDefinition().sortable);

    private getSortAction(columnDefinition: ColumnDef<any, any>, currentSort: SortInfo): ActionItem {
        const isSelected = currentSort?.field === fieldKeyToDefaultSortOptionKey(columnDefinition.toString());
        const sortDirection = isSelected ? currentSort.direction : this.DEFAULT_SORT_DIRECTION;

        const label = this.getTooltip(columnDefinition, sortDirection, isSelected);
        const style = isSelected ? ActionItemStyle.PureSquareIconButtonLight : ActionItemStyle.PureSquareIconButton;
        const icon = sortDirection === SortDirection.DESC ? 'arrow-down' : 'arrow-up';
        const eventListener = () => this.toggleSort(columnDefinition.key.toString(), isSelected, currentSort.direction);

        const modifiers: ActionItemModifier[] = [ActionItemModifier.Small];

        return {
            label,
            style,
            modifiers,
            icon,
            eventListener
        };
    }

    private toggleSort(sortField: string, isSelected: boolean, currentSortDirection: SortDirection): void {
        const newSortDirection: SortDirection = !isSelected
            ? this.DEFAULT_SORT_DIRECTION
            : this.flipSortDirection(currentSortDirection);

        // MED-5618: Convert Field keys to map correctly to default sort options
        const newSortInfo: SortInfo = {
            field: fieldKeyToDefaultSortOptionKey(sortField),
            direction: newSortDirection
        };

        this.sortInfoChanged.emit(newSortInfo);
    }

    private flipSortDirection(sortDirection: SortDirection): SortDirection {
        return sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;
    }

    private getTooltip(
        columnDefinition: ColumnDef<any, any>,
        sortDirection: SortDirection,
        isSelected: boolean
    ): string {
        const fieldSortLabel = `${columnDefinition.translateLabel} (${this.getSortDirectionLabel(columnDefinition.sortType, sortDirection)})`;

        return isSelected
            ? this.translateService.translate('sort.state', { field: fieldSortLabel })
            : `${this.translateService.translate('sort.prefix')} ${fieldSortLabel}`;
    }

    private getSortDirectionLabel(sortType: SortType, sortDirection: SortDirection) {
        return this.translateService.translate(SortOptionsHelper.getSortDirectionLabelKey(sortType, sortDirection));
    }
}
